import React, { lazy, Suspense, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { css } from '@emotion/core'
import * as FS from '@fullstory/browser'

import useDidMount from '@divvy-web/hooks.usedidmount'
import Spinner from '@divvy-web/skylab.spinner'

import { useAuth } from '@sputnik/auth'
import PageViewWrapper from '@sputnik/components/PageViewWrapper/PageViewWrapper'
import TimeoutWarningModal from '@sputnik/components/TimeoutWarningModal'
import { useBillSessionInfo } from '@sputnik/hooks/useBillSessionInfo'
import { useKeepNeoSessionAlive } from '@sputnik/hooks/useKeepNeoSessionAlive'
import GlobalTopNav from '@sputnik/remotes/GlobalTopNav/GlobalTopNav'
import { PAGE_NAME_STRINGS } from '@sputnik/resources/constants'
import { getBillApolloClient } from '@sputnik/utils/billApolloUtils'
import { logInfo } from '@sputnik/utils/loggerUtils'

import DelayedLoader from './DelayedLoader'
import { GetApplicationStatuses } from './gql/GetApplicationStatuses.gql'
import StatusErrorTile from './Status/StatusErrorTile/StatusErrorTile'

const DashboardApplication = lazy(() => import('./Status/DashboardApplication'))

const Dashboard = () => {
  const { email } = useAuth()

  useDidMount(() => {
    window.sessionStorage.removeItem('submissionPendingUrl')
  })

  const { data, error, loading, refetch } = useQuery(GetApplicationStatuses, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const applications = useMemo(
    () =>
      [].concat(data?.creditApplications ?? []).sort((app1, app2) => {
        if (app1.statusUpdatedAt && !app2.statusUpdatedAt) {
          return -1
        }
        if (!app1.statusUpdatedAt && app2.statusUpdatedAt) {
          return 1
        }
        if (app1.statusUpdatedAt > app2.statusUpdatedAt) {
          return -1
        }
        if (app1.statusUpdatedAt < app2.statusUpdatedAt) {
          return 1
        }

        return 0
      }),
    [data?.creditApplications],
  )

  const { PAGE_DASHBOARD } = PAGE_NAME_STRINGS

  const hasApplications = applications?.length > 0
  const canStartNewApplication = !!data?.salesforceContactExists || !hasApplications

  const bdcOrgId = applications?.[0]?.bdcOrgId
  const billApolloClient = useMemo(() => getBillApolloClient(bdcOrgId), [bdcOrgId])

  const { data: sessionInfoData } = useBillSessionInfo(billApolloClient)

  const shouldTopNavBeVisible = sessionInfoData?.ossType === 'bdcUp' || sessionInfoData?.isConsole
  const currentCompanyName = sessionInfoData?.organizationName || applications?.[0]?.businessInfo?.legalBusinessName

  useKeepNeoSessionAlive(bdcOrgId)

  useEffect(() => {
    if (!loading) {
      const appIdsArray = applications.map(({ salesforceCreditId }) => salesforceCreditId) ?? []

      if (FS.isInitialized()) {
        FS.identify(email)
        FS.event('Dashboard', { sf_credit_id_strs: appIdsArray })
      }

      logInfo('dashboardPageLoad', {
        attributes: {
          ...(appIdsArray.length > 0 && { creditAppIds: appIdsArray }),
          applicationCount: appIdsArray.length,
          result: `Dashboard page loaded with ${appIdsArray.length} application(s)`,
        },
        eventName: 'PageLoad',
      })
    }
  }, [email, loading, applications])

  if (loading) {
    return <Spinner fullPage />
  }

  return (
    <>
      {shouldTopNavBeVisible && (
        <GlobalTopNav
          apolloClient={billApolloClient}
          bdcOrgId={bdcOrgId}
          companyName={currentCompanyName}
          isConsole={sessionInfoData?.isConsole || false}
        />
      )}
      <PageViewWrapper
        bdcOrgId={bdcOrgId}
        canStartNewApplication={canStartNewApplication}
        pageName={PAGE_DASHBOARD}
      >
        <Suspense fallback={<DelayedLoader centered />}>
          {(!hasApplications || error) && <StatusErrorTile refetch={refetch} />}
          {hasApplications && (
            <div
              className='applications-container'
              css={appsContainerCss}
            >
              {applications.map((creditApp) => (
                <DashboardApplication
                  key={creditApp?.salesforceCreditId}
                  creditApp={creditApp}
                  refetch={refetch}
                />
              ))}
            </div>
          )}
          <TimeoutWarningModal pageName={PAGE_DASHBOARD} />
        </Suspense>
      </PageViewWrapper>
    </>
  )
}

const appsContainerCss = ({ mq }) => css`
  display: flex;
  flex-direction: column;
  gap: var(--tri-space-1200);

  ${mq.mediumMaxWidth({ gap: 'var(--tri-space-500)' })}
`

export default Dashboard
